// docs/140 DT14 — every dungeon trap moment drawn on the board: each act of a running trap gets its
// own look over the squares it touched (water rising, a boulder rolling with dust and a rumble,
// darts down the lane, a gout of flame, walls grinding in, the ceiling's dust, spear tips, the floor
// dropping away, the lights dying, a gong's shockwave, a cage dropping…). The sim says what happened
// and where (`SnareActed`); this draws it. Commissioned frames (`snare-*` in the puzzle atlas) are
// used where the atlas has them, else a clear vector drawing, so nothing is ever invisible.
import Phaser from 'phaser';
import type { ActKind, Effect, Tile } from '../../core/sim/types';
import { PUZZLE_ATLAS } from './puzzleArt';
import { LIQUID_TINT, SNARE_FX, actOf, elementTint, floodLiquid, hurtElement, jetElement, rollerFrame, rollerOf, snareDef } from '../../core/snares/snares';
import type { SfxKey } from '../../core/audio/keys.gen';
import type { UnitSprite } from './UnitSprite';

export interface SnareFxHost {
  scene: Phaser.Scene;
  /** On the ground, under the figures. */
  addGround(o: Phaser.GameObjects.GameObject): void;
  /** Over everything on the board. */
  add(o: Phaser.GameObjects.GameObject): void;
  cell: number;
  unit(id: number): UnitSprite | undefined;
  /** Shake the board (the host keeps it still under reduce motion). */
  shake(ms: number, intensity: number): void;
  /** Whether the party sees square `t` now (the blind crawl fog: nothing is drawn where it does not). */
  sees(t: Tile): boolean;
  /** The board's size in pixels. */
  boardSize(): { w: number; h: number };
  /** A word over a square. */
  floatText(t: Tile, text: string, colour: string, lift?: number): void;
  /** docs/140 DT7: hide the board's own drawing of the thing on these squares while the roller rolls. */
  hideThings?(tiles: readonly Tile[]): void;
  /** The gallery plays sounds itself (no server cues); live play leaves them to the server. */
  sound?(key: SfxKey, unit?: number): void;
  reduced(): boolean;
}

const MOTE = 'fk-snare-mote';
/** The art helper's seamless liquid textures (opaque: drawn at an alpha of our own). */
const LIQUID_FRAME: Record<string, string> = { Lava: 'snare-fx-lava', Ooze: 'snare-fx-ooze', Ale: 'snare-fx-ale', Sand: 'snare-fx-sand' };
type ActEffect = Extract<Effect, { kind: 'SnareActed' }>;

/**
 * Sounds the gallery plays for each act: the banks live play hears from the server (fk-sfx
 * `snare_acted`), so the book previews the real thing. A Jet's is its element's (here the fire jet's);
 * Shove, Hurt and Afflict have none of their own (their blow or effect sounds).
 */
const ACT_SOUND: Partial<Record<ActKind, SfxKey>> = {
  Flood: 'trap.water_rise', Roll: 'trap.roll', Volley: 'trap.darts', Jet: 'move.fire', Close: 'trap.walls', Press: 'trap.rumble',
  Spikes: 'trap.spikes', Crumble: 'trap.crumble', Snuff: 'trap.whoosh', Swing: 'trap.blade',
  Double: 'trap.warp', Gift: 'stash.open', Mend: 'spell.heal', Alarm: 'trap.alarm', Spin: 'trap.whoosh', Swap: 'trap.warp',
  Polymorph: 'trap.polymorph', Hoist: 'trap.net', Quake: 'trap.rumble', Shift: 'trap.walls', Cage: 'trap.cage', Bar: 'mech.door',
  Warp: 'trap.warp', Loose: 'trap.roar', Haze: 'spell.smoke', Kindle: 'trap.crackle', Spill: 'body.splat', Stopped: 'mech.lever', Undone: 'trap.drain',
};

export class SnareFx {
  private readonly live = new Set<Phaser.GameObjects.GameObject>();
  constructor(private readonly h: SnareFxHost) {}

  private get scene(): Phaser.Scene { return this.h.scene; }
  private get c(): number { return this.h.cell; }
  private centre(t: Tile): { x: number; y: number } { return { x: t.x * this.c + this.c / 2, y: t.y * this.c + this.c / 2 }; }
  private seen(tiles: readonly Tile[]): Tile[] { return tiles.filter((t) => this.h.sees(t)); }

  private timers: Phaser.Time.TimerEvent[] = [];

  /** Everything still drawing goes, and nothing waiting starts (a skipped playback, a new board). */
  clear(): void {
    for (const t of this.timers) t.remove(false);
    this.timers = [];
    for (const o of this.live) o.destroy();
    this.live.clear();
  }

  /** Runs `fn` after `ms` of scene time, unless `clear` comes first. */
  private after(ms: number, fn: () => void): void {
    const t = this.scene.time.delayedCall(Math.max(0, ms), () => { this.timers = this.timers.filter((x) => x !== t); fn(); });
    this.timers.push(t);
  }

  private keep<T extends Phaser.GameObjects.GameObject>(o: T, ground = false): T {
    if (ground) this.h.addGround(o); else this.h.add(o);
    this.live.add(o);
    o.once(Phaser.GameObjects.Events.DESTROY, () => this.live.delete(o));
    return o;
  }

  private gfx(ground = false): Phaser.GameObjects.Graphics { return this.keep(this.scene.add.graphics(), ground); }

  private hasFrame(frame: string): boolean {
    return this.scene.textures.exists(PUZZLE_ATLAS.key) && this.scene.textures.get(PUZZLE_ATLAS.key).has(frame);
  }

  /** A commissioned frame at `p`, a square across (×`scale`), if the atlas has it. */
  private art(frame: string, p: { x: number; y: number }, scale = 1): Phaser.GameObjects.Image | undefined {
    if (!this.hasFrame(frame)) return undefined;
    return this.keep(this.scene.add.image(p.x, p.y, PUZZLE_ATLAS.key, frame).setDisplaySize(this.c * scale, this.c * scale));
  }

  /** Runs `paint(k)` from 0 to 1 over `ms`, then destroys `objs`. */
  private run(ms: number, paint: (k: number) => void, objs: Phaser.GameObjects.GameObject[], ease = 'Linear', after?: () => void): void {
    const st = { k: 0 };
    paint(0);
    this.scene.tweens.add({ targets: st, k: 1, duration: Math.max(1, ms), ease, onUpdate: () => paint(st.k), onComplete: () => { paint(1); for (const o of objs) o.destroy(); after?.(); } });
  }

  private mote(): string {
    if (!this.scene.textures.exists(MOTE)) {
      const g = this.scene.make.graphics({}, false);
      g.fillStyle(0xffffff, 1).fillCircle(4, 4, 4);
      g.generateTexture(MOTE, 8, 8);
      g.destroy();
    }
    return MOTE;
  }

  /** A burst of motes at `p`: dust, sparkles, smoke, spray. */
  private puff(p: { x: number; y: number }, tint: number | number[], n: number, opts: { speed?: number; up?: boolean; life?: number; scale?: number; gravity?: number; ground?: boolean } = {}): void {
    if (this.h.reduced()) n = Math.ceil(n / 3);
    const c = this.c;
    const e = this.keep(this.scene.add.particles(0, 0, this.mote(), {
      emitting: false, lifespan: opts.life ?? 650, speed: { min: c * 0.6 * (opts.speed ?? 1), max: c * 2.2 * (opts.speed ?? 1) },
      angle: opts.up ? { min: -160, max: -20 } : { min: 0, max: 360 }, scale: { start: Math.max(0.35, (c / 22) * (opts.scale ?? 1)), end: 0.05 },
      alpha: { start: 0.95, end: 0 }, tint, gravityY: opts.gravity ?? 0,
    }), opts.ground);
    e.explode(n, p.x, p.y);
    this.after((opts.life ?? 650) + 200, () => e.destroy());
  }

  /** Straight runs in a list of squares (each lane of a volley, each wall of a closing room). */
  private runs(tiles: readonly Tile[]): Tile[][] {
    const out: Tile[][] = [];
    for (const t of tiles) {
      const cur = out[out.length - 1];
      const last = cur?.[cur.length - 1];
      if (cur && last && Math.abs(last.x - t.x) + Math.abs(last.y - t.y) === 1 && (cur.length < 2 || (cur[1]!.x - cur[0]!.x === t.x - last.x && cur[1]!.y - cur[0]!.y === t.y - last.y))) cur.push(t);
      else out.push([t]);
    }
    return out;
  }

  private ring(p: { x: number; y: number }, colour: number, from: number, to: number, ms: number, width = 2, ground = false): void {
    const g = this.gfx(ground);
    this.run(ms, (k) => { g.clear(); g.lineStyle(Math.max(1, width * (1 - k * 0.6)), colour, 1 - k).strokeCircle(p.x, p.y, from + (to - from) * k); }, [g], 'Quad.easeOut');
  }

  /** A short tag over a square (STOPPED!, the trap is spent). */
  tag(t: Tile, text: string, colour = '#f4cf78'): void { if (this.h.sees(t)) this.h.floatText(t, text, colour, 1); }

  // -----------------------------------------------------------------------------------------
  // The spring and the small moments
  // -----------------------------------------------------------------------------------------

  /** The anchor snaps: a red ring and a crack of dust where the trap is (the card is the host's). */
  sprung(t: Tile, ms: number, card: boolean): void {
    if (!this.h.sees(t)) return;
    const p = this.centre(t), c = this.c;
    this.ring(p, card ? 0xb48cff : 0xff5a4f, c * 0.2, c * 1.6, Math.min(900, ms * 0.6), 3);
    this.puff(p, card ? [0xb48cff, 0xe0d0ff] : [0xbfae92, 0x8a7e6a], 14, { up: true, gravity: c * 3 });
    if (!card) this.h.shake(160, 0.003);
  }

  /** A found plate or wire: a gold glint rings it. */
  found(tiles: readonly Tile[], ms: number): void {
    for (const t of this.seen(tiles).slice(0, 6)) this.ring(this.centre(t), 0xffd166, this.c * 0.1, this.c * 0.9, ms, 2, true);
  }

  /** The trap is spent: a soft settle of dust. */
  ended(t: Tile): void { if (this.h.sees(t)) this.puff(this.centre(t), [0xc9bfa8], 6, { speed: 0.4, life: 900 }); }

  // -----------------------------------------------------------------------------------------
  // The acts
  // -----------------------------------------------------------------------------------------

  /** One beat of a running trap (`def`: its book entry; `anchor`: where it sprang). */
  act(e: ActEffect, def: number, anchor: Tile | undefined, ms: number): void {
    const tiles = e.tiles.length ? e.tiles : anchor ? [anchor] : [];
    const units = e.units.map((u) => this.h.unit(u)).filter((u): u is UnitSprite => !!u);
    const s = ACT_SOUND[e.act];
    if (s && (this.seen(tiles).length || units.some((u) => this.h.sees(u.tile)))) this.h.sound?.(s, e.units[0]);
    switch (e.act) {
      case 'Flood': return this.flood(tiles, floodLiquid(def), ms);
      case 'Undone': return this.undone(tiles, def, ms);
      case 'Roll': return this.roll(tiles, rollerOf(def), ms);
      case 'Volley': return this.volley(tiles, ms);
      case 'Jet': return this.jet(tiles, jetElement(def), ms);
      case 'Close': return this.close(tiles, ms);
      case 'Press': return this.press(tiles, ms);
      case 'Spikes': return this.spikes(tiles, ms);
      case 'Crumble': return this.crumble(tiles, ms);
      case 'Snuff': return this.snuff(tiles, ms);
      case 'Afflict': return units.forEach((u) => this.swirl(u, 0xb48cff, ms));
      case 'Shove': return units.forEach((u) => this.streak(u, anchor, ms));
      case 'Swing': return this.swing(tiles, ms);
      // docs/140: the Golden Shower's blow is a rain of coins
      case 'Hurt': if (snareDef(def)?.key === 'golden_shower') { const t = units.length ? units[0]!.tile : this.seen(tiles)[0]; if (t) this.coins(t, ms); return; } { const el = elementTint(hurtElement(def)); units.forEach((u) => { this.burst(this.chest(u), el.core, el.edge, ms); }); if (!units.length) this.seen(tiles).forEach((t) => this.burst(this.centre(t), el.core, el.edge, ms)); return; }
      case 'Double': return this.double(units, tiles, ms);
      case 'Gift': return this.gift(tiles, Number(actOf(def, 'Gift')?.['gold'] ?? 0), ms);
      case 'Mend': return units.forEach((u) => this.mend(u, ms));
      case 'Alarm': return this.alarm(anchor ?? tiles[0], ms);
      case 'Spin': return this.spin(tiles, ms);
      case 'Swap': case 'Polymorph': return units.forEach((u) => this.smoke(u, e.act === 'Swap' ? 0xb48cff : 0xd8d0e8, ms));
      case 'Hoist': return units.forEach((u) => this.hoist(u, ms));
      case 'Quake': return this.quake(tiles, ms);
      case 'Shift': return this.shift(tiles, ms);
      case 'Cage': return units.forEach((u) => this.cage(u, ms));
      case 'Bar': return this.bar(tiles, ms);
      case 'Warp': return units.forEach((u) => this.swirl(u, 0x8f6bff, ms));
      case 'Loose': return this.seen(tiles).slice(0, 8).forEach((t) => this.puff(this.centre(t), [0x6b5f55, 0x9a8c7a], 10, { up: true, gravity: this.c * 2 }));
      case 'Spill': return this.seen(tiles).slice(0, 10).forEach((t) => this.puff(this.centre(t), [0x6fa8dc, 0xd8ecff], 6, { speed: 0.6 }));
      case 'Haze': return this.seen(tiles).slice(0, 10).forEach((t) => this.puff(this.centre(t), [0xb8b0c8, 0x8a8298], 6, { speed: 0.3, life: 1200, scale: 2 }));
      case 'Kindle': return this.seen(tiles).slice(0, 10).forEach((t) => this.puff(this.centre(t), [0xff7a1a, 0xffd166, 0xff3b1a], 8, { up: true, gravity: -this.c }));
      case 'Stopped': { const t = anchor ?? tiles[0]; if (t) { this.tag(t, 'STOPPED!', '#9fe08a'); this.ring(this.centre(t), 0x9fe08a, this.c * 0.3, this.c * 2.4, ms, 3); } return; }
    }
  }

  private chest(u: UnitSprite): { x: number; y: number } { return { x: u.x, y: u.y - this.c * 0.45 * u.size }; }

  /** Water (or lava, sand, ooze, ale, tar, snow) rises over the squares: each fills from its foot up, a bright edge riding the top. */
  private flood(tiles: readonly Tile[], liquid: string, ms: number): void {
    const tint = LIQUID_TINT[liquid] ?? LIQUID_TINT['Water']!;
    const shown = this.seen(tiles);
    if (!shown.length) return;
    const g = this.gfx(true), c = this.c;
    const ordered = [...shown];
    this.run(ms * 1.1, (k) => {
      g.clear();
      ordered.forEach((t, i) => {
        const local = Math.max(0, Math.min(1, k * 1.6 - (i / Math.max(1, ordered.length)) * 0.6));
        if (local <= 0) return;
        const x = t.x * c, y = t.y * c, hgt = c * local;
        g.fillStyle(tint.fill, 0.75 * (1 - Math.max(0, k - 0.85) / 0.15 * 0.6)).fillRect(x, y + c - hgt, c, hgt);
        const wave = Math.sin((k * 8 + t.x * 0.9 + t.y * 0.4) * Math.PI) * c * 0.06;
        g.lineStyle(1.5, tint.edge, 0.9).lineBetween(x, y + c - hgt + wave, x + c, y + c - hgt - wave);
        if (tint.glow) g.fillStyle(0xffe08a, 0.25 * local).fillCircle(x + c / 2, y + c - hgt / 2, c * 0.3);
      });
    }, [g], 'Sine.easeOut');
    // the commissioned liquids (lava, ooze, ale, sand) tile seamlessly: each square's rises under the edge
    const tex = LIQUID_FRAME[liquid];
    if (tex && this.hasFrame(tex)) {
      const imgs = ordered.map((t) => this.keep(this.scene.add.image(t.x * c, t.y * c, PUZZLE_ATLAS.key, tex).setOrigin(0, 0).setDisplaySize(c, c).setAlpha(0.7), true));
      const fh = imgs[0]!.frame.height, fw = imgs[0]!.frame.width;
      this.run(ms * 1.1, (k) => imgs.forEach((img, i) => {
        const local = Math.max(0, Math.min(1, k * 1.6 - (i / Math.max(1, ordered.length)) * 0.6));
        img.setCrop(0, fh * (1 - local), fw, fh * local).setAlpha(0.7 * (k > 0.85 ? 1 - (k - 0.85) / 0.15 * 0.6 : 1));
      }), imgs, 'Sine.easeOut');
    }
    for (const t of ordered.slice(0, 6)) this.puff(this.centre(t), [tint.edge, tint.fill], 5, { up: true, gravity: c * 4, speed: 0.6 });
    if (liquid === 'Lava') this.h.shake(200, 0.002);
  }

  /** What a beat changed is put back: water drains (a sinking sheen), bars lift, walls grind home. */
  private undone(tiles: readonly Tile[], def: number, ms: number): void {
    const shown = this.seen(tiles);
    if (!shown.length) return;
    const tint = snareDef(def)?.beats.some((b) => b.kind === 'Flood') ? (LIQUID_TINT[floodLiquid(def)] ?? LIQUID_TINT['Water']!) : { fill: 0x9a8c7a, edge: 0xd8cfbe };
    const g = this.gfx(true), c = this.c;
    this.run(ms, (k) => {
      g.clear();
      for (const t of shown) { const hgt = c * (1 - k); g.fillStyle(tint.fill, 0.45 * (1 - k)).fillRect(t.x * c, t.y * c + c - hgt, c, hgt); }
    }, [g], 'Sine.easeIn');
  }

  /** The roller comes out and rolls along its squares, turning, throwing up dust, the board rumbling under it. */
  private roll(tiles: readonly Tile[], thing: string, ms: number): void {
    if (!tiles.length) return;
    const c = this.c;
    const pts = tiles.map((t) => this.centre(t));
    const look = ROLLERS[thing] ?? ROLLERS['Boulder']!;
    const size = c * look.size;
    // the board draws the roller's thing where it stood; this drawing takes over while it rolls
    this.h.hideThings?.([tiles[0]!]);
    const art = this.art(rollerFrame(thing), pts[0]!, look.size);
    const g = art ? undefined : this.gfx();
    const box = g ? this.keep(this.scene.add.container(pts[0]!.x, pts[0]!.y, [g])) : undefined;
    if (box) { this.live.delete(g!); this.h.add(box); drawRoller(g!, thing, size); }
    const body = (art ?? box)!;
    const shadow = this.gfx(true);
    let lastTile = -1;
    this.run(ms, (k) => {
      const f = k * (pts.length - 1);
      const i = Math.min(pts.length - 2, Math.floor(f));
      const a = pts[Math.max(0, i)]!, b = pts[Math.min(pts.length - 1, i + 1)] ?? a;
      const u = pts.length === 1 ? 0 : f - Math.max(0, i);
      const x = a.x + (b.x - a.x) * u, y = a.y + (b.y - a.y) * u;
      const visible = this.h.sees(tiles[Math.min(tiles.length - 1, Math.round(f))]!);
      body.setPosition(x, y - (look.spin && (!art || look.round) ? 0 : Math.abs(Math.sin(k * Math.PI * pts.length)) * c * 0.06)).setVisible(visible);
      if (look.spin && (!art || look.round)) body.setAngle(k * 360 * Math.max(1, pts.length) * (b.x < a.x || b.y < a.y ? -1 : 1) * 0.5);
      shadow.clear(); if (visible) shadow.fillStyle(0x000000, 0.3).fillEllipse(x, y + size * 0.42, size * 0.9, size * 0.28);
      const cur = Math.round(f);
      if (cur !== lastTile) {
        lastTile = cur;
        if (visible) { this.puff({ x, y: y + size * 0.4 }, [0xbfae92, 0x8a7e6a], 5, { up: true, gravity: c * 3, speed: 0.6, ground: true }); this.h.shake(90, look.heavy ? 0.004 : 0.002); }
      }
    }, [body, shadow], 'Linear');
  }

  /** Darts fly down each lane, a few close together, glinting. */
  private volley(tiles: readonly Tile[], ms: number): void {
    const c = this.c;
    for (const lane of this.runs(tiles)) {
      if (!this.seen(lane).length) continue;
      const a = this.centre(lane[0]!), b = this.centre(lane[lane.length - 1]!);
      const ang = Math.atan2(b.y - a.y, b.x - a.x);
      for (let n = 0; n < 3; n++) {
        const off = (n - 1) * c * 0.18;
        const ox = -Math.sin(ang) * off, oy = Math.cos(ang) * off;
        const art = this.art(SNARE_FX.dart, a, 0.6);
        const g = art ? undefined : this.gfx();
        if (g) { g.lineStyle(1.5, 0xd8d0c0, 1).lineBetween(-c * 0.22, 0, c * 0.18, 0); g.fillStyle(0xffffff, 1).fillTriangle(c * 0.18, -2, c * 0.3, 0, c * 0.18, 2); g.fillStyle(0xd0574a, 1).fillTriangle(-c * 0.22, -2.5, -c * 0.12, 0, -c * 0.22, 2.5); }
        const o = (art ?? g)!;
        o.setRotation(ang).setVisible(false);
        this.after(n * ms * 0.12, () => {
          o.setVisible(true);
          this.run(ms * 0.55, (k) => { o.setPosition(a.x + ox + (b.x - a.x) * k, a.y + oy + (b.y - a.y) * k); }, [o], 'Quad.easeIn', () => this.puff({ x: b.x, y: b.y }, [0xffffff, 0xd8d0c0], 4, { speed: 0.5 }));
        });
      }
    }
  }

  /** A gout of flame (or frost, or radiance) roars down each lane and dies back. */
  private jet(tiles: readonly Tile[], element: string, ms: number): void {
    const { core, edge } = elementTint(element);
    const c = this.c;
    for (const lane of this.runs(tiles)) {
      const shown = this.seen(lane);
      if (!shown.length) continue;
      const g = this.gfx();
      const pts = lane.map((t) => this.centre(t));
      this.run(ms, (k) => {
        g.clear();
        const reach = Math.min(1, k * 2.2) * pts.length;
        const fade = k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1;
        for (let i = 0; i < pts.length; i++) {
          if (i > reach || !this.h.sees(lane[i]!)) continue;
          const p = pts[i]!, w = c * (0.35 + 0.15 * Math.sin(k * 30 + i)) * (0.6 + 0.4 * (i / pts.length));
          g.fillStyle(core, 0.55 * fade).fillCircle(p.x, p.y, w);
          g.fillStyle(edge, 0.7 * fade).fillCircle(p.x, p.y, w * 0.45);
        }
      }, [g], 'Sine.easeOut');
      this.puff(pts[0]!, [core, edge], 10, { speed: 1.4 });
    }
  }

  /** Stone slabs grind in onto the squares from the walls; the board shakes, grit spills. */
  private close(tiles: readonly Tile[], ms: number): void {
    const shown = this.seen(tiles);
    if (!shown.length) return;
    const c = this.c;
    if (this.hasFrame(SNARE_FX.slab)) {
      const imgs = shown.map((t) => this.keep(this.scene.add.image(t.x * c + c / 2, t.y * c + c / 2, PUZZLE_ATLAS.key, SNARE_FX.slab).setDisplaySize(c, c)));
      const base = imgs[0]!.scaleX;
      this.run(ms, (k) => imgs.forEach((img) => img.setScale(base * Math.min(1, k * 1.3), img.scaleY)), [], 'Quad.easeIn', () => this.after(200, () => imgs.forEach((i) => i.destroy())));
    } else {
    const g = this.gfx();
    this.run(ms, (k) => {
      g.clear();
      for (const t of shown) {
        const x = t.x * c, y = t.y * c, w = c * Math.min(1, k * 1.3);
        g.fillStyle(0x6b6358, 0.95).fillRect(x + (c - w) / 2, y, w, c);
        g.lineStyle(1, 0x3a3530, 1).strokeRect(x + (c - w) / 2 + 0.5, y + 0.5, w - 1, c - 1);
        g.lineStyle(1, 0x8a8174, 0.8).lineBetween(x + (c - w) / 2 + 2, y + c * 0.33, x + (c + w) / 2 - 2, y + c * 0.33).lineBetween(x + (c - w) / 2 + 2, y + c * 0.66, x + (c + w) / 2 - 2, y + c * 0.66);
      }
    }, [g], 'Quad.easeIn');
    }
    this.h.shake(ms * 0.8, 0.005);
    for (const t of shown.slice(0, 6)) this.puff(this.centre(t), [0x9a8c7a, 0x6b5f55], 6, { up: true, gravity: c * 3 });
  }

  /** The ceiling comes down: dust sifts over the squares and they darken in a squeeze. */
  private press(tiles: readonly Tile[], ms: number): void {
    const shown = this.seen(tiles);
    if (!shown.length) return;
    const g = this.gfx(), c = this.c;
    this.run(ms, (k) => {
      g.clear();
      const a = 0.45 * Math.sin(k * Math.PI);
      for (const t of shown) g.fillStyle(0x000000, a).fillRect(t.x * c, t.y * c, c, c);
    }, [g], 'Sine.easeInOut');
    for (const t of shown.filter((_, i) => i % 2 === 0).slice(0, 10)) this.puff({ x: t.x * c + c / 2, y: t.y * c }, [0xc9bfa8, 0x9a8c7a], 4, { speed: 0.2, gravity: c * 6, life: 700 });
    this.h.shake(ms * 0.6, 0.003);
  }

  /** Spear tips pop up through the floor on the squares, hold a breath, and sink. */
  private spikes(tiles: readonly Tile[], ms: number): void {
    const c = this.c;
    for (const t of this.seen(tiles)) {
      const p = this.centre(t);
      const art = this.art(SNARE_FX.spikes, p);
      const g = art ? undefined : this.gfx();
      if (g) for (const [dx, dy] of [[-0.25, 0.15], [0.05, -0.1], [0.28, 0.2], [-0.05, 0.32]] as const) {
        const x = dx * c, y = dy * c;
        g.fillStyle(0xd8d8e0, 1).fillTriangle(x - c * 0.07, y + c * 0.1, x + c * 0.07, y + c * 0.1, x, y - c * 0.22);
        g.lineStyle(1, 0x4a4a55, 1).strokeTriangle(x - c * 0.07, y + c * 0.1, x + c * 0.07, y + c * 0.1, x, y - c * 0.22);
      }
      const o = (art ?? g)!;
      if (g) g.setPosition(p.x, p.y);
      o.setScale(o.scaleX, 0.01);
      const sx = o.scaleX, sy = art ? art.scaleY || 1 : 1;
      this.scene.tweens.chain({ targets: o, tweens: [
        { scaleY: art ? (c / art.height) : sy, duration: Math.max(1, ms * 0.15), ease: 'Back.easeOut' },
        { scaleY: art ? (c / art.height) : sy, duration: Math.max(1, ms * 0.5) },
        { scaleY: 0.01, duration: Math.max(1, ms * 0.25), ease: 'Quad.easeIn', onComplete: () => o.destroy() },
      ] });
      void sx;
      this.puff({ x: p.x, y: p.y + c * 0.2 }, [0x9a8c7a], 3, { up: true, speed: 0.5, gravity: c * 4 });
    }
  }

  /** Cracks run across the squares, then they fall away into the dark. */
  private crumble(tiles: readonly Tile[], ms: number): void {
    const shown = this.seen(tiles);
    if (!shown.length) return;
    const g = this.gfx(true), c = this.c;
    this.run(ms, (k) => {
      g.clear();
      for (const t of shown) {
        const x = t.x * c, y = t.y * c;
        if (k < 0.45) {
          const a = k / 0.45;
          g.lineStyle(1, 0x1a1410, 0.9).lineBetween(x + c * 0.2, y + c * 0.1, x + c * (0.2 + 0.3 * a), y + c * (0.1 + 0.5 * a)).lineBetween(x + c * 0.5, y + c * 0.5, x + c * (0.5 + 0.35 * a), y + c * (0.5 + 0.2 * a)).lineBetween(x + c * 0.5, y + c * 0.5, x + c * (0.5 - 0.3 * a), y + c * (0.5 + 0.4 * a));
        } else {
          const a = (k - 0.45) / 0.55, s = c * (1 - a * 0.7);
          g.fillStyle(0x050407, Math.min(1, a * 1.5)).fillRect(x, y, c, c);
          g.fillStyle(0x6b6358, 1 - a).fillRect(x + (c - s) / 2, y + (c - s) / 2 + a * c * 0.2, s, s);
        }
      }
    }, [g], 'Linear');
    this.after(ms * 0.45, () => { this.h.shake(220, 0.004); for (const t of shown.slice(0, 6)) this.puff(this.centre(t), [0x9a8c7a, 0x6b5f55], 8, { gravity: c * 5 }); });
  }

  /** The lights die: smoke curls off them and the chamber goes dark (the light map follows the board). */
  private snuff(tiles: readonly Tile[], ms: number): void {
    const { w, h } = this.h.boardSize();
    const g = this.gfx();
    this.run(ms * 1.4, (k) => { g.clear(); g.fillStyle(0x02030a, 0.55 * Math.sin(Math.min(1, k * 1.2) * Math.PI * 0.5) * (k > 0.8 ? (1 - k) / 0.2 : 1)).fillRect(0, 0, w, h); }, [g]);
    for (const t of this.seen(tiles).slice(0, 8)) this.puff(this.centre(t), [0x5a5560, 0x8a8298], 5, { up: true, speed: 0.3, life: 1200, gravity: -this.c });
  }

  /** A swirl of motes about a figure (a curse, a warp). */
  private swirl(u: UnitSprite, colour: number, ms: number): void {
    if (!this.h.sees(u.tile)) return;
    const p = this.chest(u), c = this.c, g = this.gfx();
    this.run(ms, (k) => {
      g.clear();
      for (let i = 0; i < 6; i++) { const a = k * Math.PI * 4 + (i / 6) * Math.PI * 2, r = c * 0.5 * (1 - k * 0.4); g.fillStyle(colour, 1 - k).fillCircle(p.x + Math.cos(a) * r, p.y + Math.sin(a) * r * 0.5, 1.6); }
    }, [g]);
  }

  /** A whoosh streak behind a shoved figure, away from the trap. */
  private streak(u: UnitSprite, from: Tile | undefined, ms: number): void {
    if (!this.h.sees(u.tile)) return;
    const p = this.chest(u), c = this.c, g = this.gfx();
    const src = from ? this.centre(from) : { x: p.x - c, y: p.y };
    const ang = Math.atan2(p.y - src.y, p.x - src.x);
    this.run(ms, (k) => {
      g.clear();
      for (let i = -1; i <= 1; i++) {
        const ox = -Math.sin(ang) * i * c * 0.15, oy = Math.cos(ang) * i * c * 0.15;
        const len = c * 0.9 * (1 - k);
        g.lineStyle(1.5, 0xf4cf78, 0.8 * (1 - k)).lineBetween(u.x + ox - Math.cos(ang) * len, u.y - c * 0.45 + oy - Math.sin(ang) * len, u.x + ox, u.y - c * 0.45 + oy);
      }
    }, [g]);
  }

  /** The blade sweeps over its lane, a pendulum arc, there and back. */
  private swing(tiles: readonly Tile[], ms: number): void {
    const c = this.c;
    for (const lane of this.runs(tiles)) {
      const shown = this.seen(lane);
      if (!shown.length) continue;
      const a = this.centre(lane[0]!), b = this.centre(lane[lane.length - 1]!);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const horiz = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y);
      const pivot = horiz ? { x: mid.x, y: mid.y - c * 1.6 } : { x: mid.x - c * 1.6, y: mid.y };
      const half = Math.max(c * 0.5, Math.hypot(b.x - a.x, b.y - a.y) / 2);
      const len = Math.hypot(half, c * 1.6);
      const span = Math.atan2(half, c * 1.6);
      const art = this.art(SNARE_FX.blade, mid, 1.2);
      const g = this.gfx();
      this.run(ms, (k) => {
        const th = Math.sin(k * Math.PI * 2) * span;
        const base = horiz ? Math.PI / 2 : 0;
        const x = pivot.x + Math.cos(base - th) * len, y = pivot.y + Math.sin(base - th) * len;
        g.clear();
        g.lineStyle(1.5, 0x4a4540, 1).lineBetween(pivot.x, pivot.y, x, y);
        // the trail of the sweep
        for (let j = 1; j <= 4; j++) { const tj = Math.sin((k - j * 0.025) * Math.PI * 2) * span; g.fillStyle(0xc9d0e0, 0.05 * (5 - j)).fillCircle(pivot.x + Math.cos(base - tj) * len, pivot.y + Math.sin(base - tj) * len, c * 0.2); }
        if (art) art.setPosition(x, y).setRotation(-th + (horiz ? 0 : Math.PI / 2));
        else { g.fillStyle(0xd8dce8, 1).slice(x, y, c * 0.45, base - th + Math.PI / 2 - 0.9, base - th + Math.PI / 2 + 0.9, false).fillPath(); g.lineStyle(1, 0x5a5f6a, 1).strokeCircle(x, y, c * 0.08); }
      }, art ? [g, art] : [g], 'Linear');
    }
  }

  private burst(p: { x: number; y: number }, core: number, edge: number, ms: number): void {
    this.ring(p, edge, this.c * 0.1, this.c * 0.9, ms * 0.6, 3);
    this.puff(p, [core, edge], 12, { speed: 1.2 });
  }

  /** A mirror's sheen over the victim, and the shadow stepping out of it. */
  private double(units: UnitSprite[], tiles: readonly Tile[], ms: number): void {
    const c = this.c;
    for (const u of units) {
      if (!this.h.sees(u.tile)) continue;
      const g = this.gfx(), p = this.chest(u);
      this.run(ms, (k) => {
        g.clear();
        const x = p.x - c * 0.5 + c * k;
        g.fillStyle(0xe0f0ff, 0.5 * Math.sin(k * Math.PI)).fillRect(x - c * 0.08, p.y - c * 0.5, c * 0.16, c);
        g.lineStyle(1, 0xc9d8ff, 0.8 * (1 - k)).strokeRect(p.x - c * 0.45, p.y - c * 0.55, c * 0.9, c * 1.1);
      }, [g]);
    }
    for (const t of this.seen(tiles).slice(0, 2)) this.puff(this.centre(t), [0x2a2240, 0x6b5f8a], 12, { speed: 0.5, life: 900 });
  }

  /** A chest pops in with a spray of sparkles (and its gold rises). */
  private gift(tiles: readonly Tile[], gold: number, ms: number): void {
    const t = this.seen(tiles)[0];
    if (!t) return;
    const p = this.centre(t), c = this.c, g = this.gfx();
    g.setPosition(p.x, p.y);
    g.fillStyle(0x7a4a22, 1).fillRect(-c * 0.32, -c * 0.12, c * 0.64, c * 0.36);
    g.fillStyle(0x9a6230, 1).fillRoundedRect(-c * 0.34, -c * 0.3, c * 0.68, c * 0.2, 2);
    g.lineStyle(1, 0xffd166, 1).strokeRect(-c * 0.32, -c * 0.12, c * 0.64, c * 0.36).lineBetween(0, -c * 0.3, 0, c * 0.24);
    g.setScale(0.1);
    this.scene.tweens.chain({ targets: g, tweens: [{ scale: 1.15, duration: Math.max(1, ms * 0.25), ease: 'Back.easeOut' }, { scale: 1, duration: Math.max(1, ms * 0.15) }, { alpha: 0, delay: ms * 0.5, duration: Math.max(1, ms * 0.3), onComplete: () => g.destroy() }] });
    this.puff(p, [0xffd166, 0xfff0b0, 0xffffff], 22, { up: true, gravity: c * 2, speed: 1.2 });
    if (gold > 0) this.h.floatText(t, `+${gold} GOLD`, '#ffd166', 1);
  }

  /** Gold coins pour down from a hatch above the square, bounce and glint. */
  private coins(t: Tile, ms: number): void {
    if (!this.h.sees(t)) return;
    const p = this.centre(t), c = this.c;
    for (let n = 0; n < 18; n++) {
      const g = this.gfx();
      const dx = (Math.sin(n * 2.399) * 0.45) * c;
      g.fillStyle(0xb8860b, 1).fillEllipse(0, 0, c * 0.2, c * 0.14).fillStyle(0xffd166, 1).fillEllipse(0, -1, c * 0.16, c * 0.1);
      g.setPosition(p.x + dx, p.y - c * 2.2).setAlpha(0);
      const land = p.y + ((n % 5) - 2) * c * 0.08;
      this.scene.tweens.chain({ targets: g, tweens: [
        { alpha: 1, y: land, angle: 180 + n * 40, delay: (n * ms * 0.5) / 18, duration: Math.max(1, ms * 0.3), ease: 'Quad.easeIn' },
        { y: land - c * 0.15, duration: Math.max(1, ms * 0.08), yoyo: true, ease: 'Quad.easeOut' },
        { alpha: 0, delay: ms * 0.15, duration: Math.max(1, ms * 0.2), onComplete: () => g.destroy() },
      ] });
    }
    this.after(ms * 0.3, () => this.puff(p, [0xffd166, 0xfff0b0], 12, { up: true, gravity: c * 2, speed: 0.8 }));
  }

  private mend(u: UnitSprite, ms: number): void {
    if (!this.h.sees(u.tile)) return;
    this.ring(this.chest(u), 0x6fe08a, this.c * 0.2, this.c * 0.8, ms * 0.8, 2);
    this.puff(this.chest(u), [0x6fe08a, 0xc8ffd0], 10, { up: true, speed: 0.5, gravity: -this.c });
  }

  /** A gong's boom: rings of sound run out across the whole depth, and the board shivers. */
  private alarm(t: Tile | undefined, ms: number): void {
    if (!t) return;
    const p = this.centre(t), c = this.c;
    const far = Math.max(this.h.boardSize().w, this.h.boardSize().h) * 0.6;
    for (let i = 0; i < 3; i++) this.after(i * ms * 0.18, () => this.ring(p, 0xffd166, c * 0.4, far, ms * 1.2, 4 - i));
    this.h.shake(ms * 0.5, 0.004);
    if (this.h.sees(t)) this.h.floatText(t, 'GONG!', '#ffd166', 1);
  }

  /** The room spins: a whirl turns over its middle. */
  private spin(tiles: readonly Tile[], ms: number): void {
    const shown = this.seen(tiles);
    if (!shown.length) return;
    const cx = shown.reduce((a, t) => a + t.x, 0) / shown.length, cy = shown.reduce((a, t) => a + t.y, 0) / shown.length;
    const p = { x: cx * this.c + this.c / 2, y: cy * this.c + this.c / 2 }, c = this.c, g = this.gfx();
    const r = Math.max(c * 1.5, Math.sqrt(shown.length) * c * 0.6);
    this.run(ms, (k) => {
      g.clear();
      for (let arm = 0; arm < 3; arm++) {
        g.lineStyle(2, 0xb48cff, 0.7 * Math.sin(k * Math.PI));
        g.beginPath();
        for (let s = 0; s <= 20; s++) { const f = s / 20, a = k * Math.PI * 4 + arm * (Math.PI * 2 / 3) + f * Math.PI * 1.5, rr = r * f; const x = p.x + Math.cos(a) * rr, y = p.y + Math.sin(a) * rr; if (s === 0) g.moveTo(x, y); else g.lineTo(x, y); }
        g.strokePath();
      }
    }, [g], 'Sine.easeInOut');
  }

  /** A puff of smoke round a figure (swapped, turned into something small). */
  private smoke(u: UnitSprite, tint: number, ms: number): void {
    if (!this.h.sees(u.tile)) return;
    this.puff(this.chest(u), [tint, 0xffffff, 0x8a8298], 18, { speed: 0.7, life: Math.max(500, ms), scale: 1.6 });
  }

  /** A net snaps up round a figure and hauls it off its feet. */
  private hoist(u: UnitSprite, ms: number): void {
    if (!this.h.sees(u.tile)) return;
    const p = this.chest(u), c = this.c;
    const art = this.art(SNARE_FX.net, p, 1.1);
    const g = art ? undefined : this.gfx();
    if (g) { g.setPosition(p.x, p.y); drawNet(g, c); }
    const o = (art ?? g)!;
    o.setScale(0.2);
    this.scene.tweens.add({ targets: o, scale: art ? (c * 1.1) / art.width : 1, y: p.y - c * 0.15, duration: Math.max(1, ms * 0.35), ease: 'Back.easeOut', onComplete: () => this.after(ms * 0.4, () => o.destroy()) });
    this.scene.tweens.add({ targets: u, y: u.y - c * 0.25, duration: Math.max(1, ms * 0.35), ease: 'Quad.easeOut' });
  }

  /** The ground heaves: everything shakes and rubble spills over the squares. */
  private quake(tiles: readonly Tile[], ms: number): void {
    this.h.shake(ms * 0.9, 0.008);
    for (const t of this.seen(tiles).filter((_, i) => i % 2 === 0).slice(0, 12)) this.puff(this.centre(t), [0x8a7e6a, 0x6b5f55, 0xbfae92], 6, { up: true, gravity: this.c * 5 });
  }

  /** False walls grind up out of the floor on the squares. */
  private shift(tiles: readonly Tile[], ms: number): void {
    const shown = this.seen(tiles);
    if (!shown.length) return;
    const g = this.gfx(), c = this.c;
    this.run(ms, (k) => {
      g.clear();
      for (const t of shown) { const hgt = c * Math.min(1, k * 1.2); g.fillStyle(0x5e574d, 0.95).fillRect(t.x * c + 1, t.y * c + c - hgt, c - 2, hgt); g.lineStyle(1, 0x2a2622, 1).strokeRect(t.x * c + 1.5, t.y * c + c - hgt + 0.5, c - 3, hgt - 1); }
    }, [g], 'Quad.easeOut');
    this.h.shake(ms * 0.6, 0.003);
  }

  /** An iron cage drops out of the dark onto a figure and bounces shut. */
  private cage(u: UnitSprite, ms: number): void {
    if (!this.h.sees(u.tile)) return;
    const p = this.chest(u), c = this.c;
    const art = this.art(SNARE_FX.cage, { x: p.x, y: p.y - c * 2 }, 1.1);
    const g = art ? undefined : this.gfx();
    if (g) { g.setPosition(p.x, p.y - c * 2); drawCage(g, c); }
    const o = (art ?? g)!;
    o.setAlpha(0.2);
    this.scene.tweens.add({ targets: o, y: p.y, alpha: 1, duration: Math.max(1, ms * 0.4), ease: 'Bounce.easeOut', onComplete: () => { this.h.shake(120, 0.003); this.after(ms * 0.4, () => o.destroy()); } });
  }

  /** Bars slam down over the doors. */
  private bar(tiles: readonly Tile[], ms: number): void {
    const c = this.c;
    for (const t of this.seen(tiles)) {
      const p = this.centre(t);
      const port = this.art(SNARE_FX.portcullis, { x: p.x, y: p.y - c });
      if (port) { port.setAlpha(0.3); this.scene.tweens.add({ targets: port, y: p.y, alpha: 1, duration: Math.max(1, ms * 0.35), ease: 'Bounce.easeOut', onComplete: () => this.after(ms * 0.4, () => port.destroy()) }); continue; }
      const g = this.gfx(), x = t.x * c, y = t.y * c;
      this.run(ms * 0.5, (k) => { g.clear(); g.fillStyle(0x2a2d33, 1); for (let i = 0; i < 4; i++) g.fillRect(x + 2 + i * (c - 4) / 4, y, 2, c * k); g.fillRect(x, y + c * k - 2, c, 2); }, [g], 'Bounce.easeOut');
    }
    if (this.seen(tiles).length) this.h.shake(120, 0.003);
  }
}

/** How each roller is drawn when the atlas has no frame for it. */
/** `round`: the commissioned frame may spin (a ball); upright ones bob as they roll. */
const ROLLERS: Record<string, { size: number; spin: boolean; heavy: boolean; round?: boolean }> = {
  Boulder: { size: 1.25, spin: true, heavy: true, round: true }, Log: { size: 1.1, spin: true, heavy: false }, OilBarrel: { size: 0.9, spin: true, heavy: false },
  PowderKeg: { size: 0.85, spin: true, heavy: false }, Wheel: { size: 1.1, spin: true, heavy: true, round: true }, Snowball: { size: 1.2, spin: true, heavy: false, round: true },
  Cart: { size: 1, spin: false, heavy: true }, Statue: { size: 1.3, spin: false, heavy: true }, Saw: { size: 1, spin: true, heavy: false, round: true },
};

/** A roller's vector look, centred on 0,0, `s` across. */
export function drawRoller(g: Phaser.GameObjects.Graphics, thing: string, s: number): void {
  const r = s / 2;
  switch (thing) {
    case 'Log':
      g.fillStyle(0x6b4a2a, 1).fillRoundedRect(-r, -r * 0.45, s, r * 0.9, r * 0.3);
      g.fillStyle(0xc9a36a, 1).fillCircle(r * 0.75, 0, r * 0.4); g.lineStyle(1, 0x6b4a2a, 1).strokeCircle(r * 0.75, 0, r * 0.22);
      break;
    case 'OilBarrel': case 'PowderKeg':
      g.fillStyle(thing === 'OilBarrel' ? 0x5a3a20 : 0x3a2a1a, 1).fillCircle(0, 0, r);
      g.lineStyle(2, 0x9a9a9a, 1).strokeCircle(0, 0, r * 0.95).strokeCircle(0, 0, r * 0.55);
      if (thing === 'PowderKeg') { g.fillStyle(0x111111, 1).fillCircle(0, 0, r * 0.3); g.lineStyle(1.5, 0xffd166, 1).lineBetween(0, -r, r * 0.3, -r * 1.3); g.fillStyle(0xff7a1a, 1).fillCircle(r * 0.32, -r * 1.32, 2); }
      else g.fillStyle(0x1a1410, 1).fillCircle(0, 0, r * 0.25);
      break;
    case 'Wheel': case 'Saw':
      g.fillStyle(0x7a7f8a, 1).fillCircle(0, 0, r * 0.85);
      for (let i = 0; i < (thing === 'Saw' ? 12 : 8); i++) { const a = (i / (thing === 'Saw' ? 12 : 8)) * Math.PI * 2; g.fillStyle(0xd8dce8, 1).fillTriangle(Math.cos(a - 0.15) * r * 0.8, Math.sin(a - 0.15) * r * 0.8, Math.cos(a + 0.15) * r * 0.8, Math.sin(a + 0.15) * r * 0.8, Math.cos(a) * r * 1.1, Math.sin(a) * r * 1.1); }
      g.fillStyle(0x3a3d44, 1).fillCircle(0, 0, r * 0.25);
      break;
    case 'Snowball':
      g.fillStyle(0xf0f6ff, 1).fillCircle(0, 0, r); g.fillStyle(0xc8d8ee, 1).fillCircle(r * 0.3, r * 0.25, r * 0.35).fillCircle(-r * 0.4, -r * 0.2, r * 0.2);
      break;
    case 'Cart':
      g.fillStyle(0x5a4a3a, 1).fillRect(-r, -r * 0.6, s, r * 0.9); g.fillStyle(0x6b6b6b, 1).fillRect(-r * 0.9, -r * 0.75, s * 0.9, r * 0.25);
      g.fillStyle(0x222222, 1).fillCircle(-r * 0.55, r * 0.4, r * 0.3).fillCircle(r * 0.55, r * 0.4, r * 0.3);
      break;
    case 'Statue':
      g.fillStyle(0x8a8478, 1).fillRect(-r * 0.4, -r * 1.1, r * 0.8, r * 1.4); g.fillCircle(0, -r * 1.25, r * 0.3);
      g.fillStyle(0x5a554c, 1).fillRect(-r * 0.7, r * 0.2, r * 1.4, r * 0.35); g.fillStyle(0x222222, 1).fillCircle(-r * 0.5, r * 0.6, r * 0.22).fillCircle(r * 0.5, r * 0.6, r * 0.22);
      break;
    default:
      g.fillStyle(0x7a7468, 1).fillCircle(0, 0, r); g.fillStyle(0x9a9284, 1).fillCircle(-r * 0.3, -r * 0.3, r * 0.4);
      g.lineStyle(1.5, 0x4a453e, 1).strokeCircle(0, 0, r).lineBetween(-r * 0.5, r * 0.2, r * 0.4, r * 0.5).lineBetween(r * 0.1, -r * 0.6, r * 0.5, -r * 0.1);
  }
}

/** A net's vector look, centred on 0,0. */
export function drawNet(g: Phaser.GameObjects.Graphics, c: number): void {
  g.lineStyle(1, 0xc9b88a, 1);
  for (let i = -2; i <= 2; i++) { g.lineBetween(-c * 0.45, i * c * 0.18, c * 0.45, i * c * 0.18 + c * 0.05); g.lineBetween(i * c * 0.18, -c * 0.5, i * c * 0.18 + c * 0.05, c * 0.45); }
  g.lineStyle(1.5, 0x8a7a5a, 1).lineBetween(0, -c * 0.5, 0, -c * 1.2);
}

/** A cage's vector look, centred on 0,0 (a figure fits in it). */
export function drawCage(g: Phaser.GameObjects.Graphics, c: number): void {
  g.lineStyle(1.5, 0x3a3d44, 1);
  for (let i = 0; i < 6; i++) { const x = -c * 0.45 + i * c * 0.18; g.lineBetween(x, -c * 0.55, x, c * 0.5); }
  g.lineStyle(2.5, 0x2a2d33, 1).lineBetween(-c * 0.5, -c * 0.55, c * 0.5, -c * 0.55).lineBetween(-c * 0.5, c * 0.5, c * 0.5, c * 0.5);
  g.lineStyle(1.5, 0x5a5f6a, 1).lineBetween(0, -c * 0.55, 0, -c * 0.9);
}
